import { useState } from "react";
import {
    HStack, VStack, Box, Image,
    DrawerRoot, DrawerBackdrop, DrawerPositioner, DrawerContent,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import TopBar from '@/components/layout/TopBar';
import { Outlet } from "react-router-dom";
import MenuItem from "@/components/ui/menu/MenuItem";
import { HiOutlineCog6Tooth, HiOutlineHome, HiOutlineUserGroup, HiOutlineBriefcase } from "react-icons/hi2";
import { PageHeaderProvider } from "@/components/layout/PageHeaderContext";
import { AgentTypesProvider, useAgentTypes } from "@/components/layout/AgentTypesContext";
import { RecruitmentDashboardDataProvider } from "@/components/layout/RecruitmentDashboardDataContext";
import { SalesDashboardDataProvider } from "@/components/layout/SalesDashboardDataContext";
import { BrandingProvider, useBranding } from "@/auth/BrandingContext";
import { AccountScoringProvider } from "@/auth/ScoringContext";

const BASE_NAV_ITEMS = [
    { url: "/models", labelKey: "nav.home", icon: <HiOutlineHome size={20} /> },
];

const RECRUITMENT_NAV_ITEM = { url: "/recruitment", labelKey: "nav.recruitment", icon: <HiOutlineUserGroup size={20} /> };
const SALES_NAV_ITEM = { url: "/sales", labelKey: "nav.sales", icon: <HiOutlineBriefcase size={20} /> };

const REST_NAV_ITEMS = [
    { url: "/account-settings", labelKey: "nav.accountSettings", icon: <HiOutlineCog6Tooth size={20} /> },
];

// Client logo replaces the default "C" badge.
const SidebarBadge = () => {
    const { branding, logoUrl } = useBranding();
    const [failedUrl, setFailedUrl] = useState(null);
    if (branding === null) return <Box w="40px" h="40px" mb={4} />;
    if (logoUrl && logoUrl !== failedUrl) {
        return (
            <Box w="40px" h="40px" borderRadius="12px" overflow="hidden" bg="white" mb={4} display="flex" alignItems="center" justifyContent="center">
                <Image src={logoUrl} alt={branding.companyName ?? ""} w="full" h="full" objectFit="contain" p={1} onError={() => setFailedUrl(logoUrl)} />
            </Box>
        );
    }
    return (
        <Box
            w="40px"
            h="40px"
            borderRadius="12px"
            bgGradient="to-r"
            gradientFrom="var(--brand-from, #9D00FF)"
            gradientTo="var(--brand-to, #8500D8)"
            display="flex"
            alignItems="center"
            justifyContent="center"
            color="var(--brand-on-primary, white)"
            fontWeight={800}
            fontSize="16px"
            mb={4}
        >
            C
        </Box>
    );
};

const SidebarNav = ({ t, onClose }) => {
    const { hasRecruiterAgents, hasSalesManagerAgents } = useAgentTypes();
    const navItems = [
        ...BASE_NAV_ITEMS,
        ...(hasRecruiterAgents ? [RECRUITMENT_NAV_ITEM] : []),
        ...(hasSalesManagerAgents ? [SALES_NAV_ITEM] : []),
        ...REST_NAV_ITEMS,
    ];

    return (
        <VStack
            bg="#131019"
            h={'100vh'}
            w="80px"
            alignItems={'center'}
            py={5}
            gap={2}
        >
            <SidebarBadge />
            <VStack w={'100%'} gap={1.5} alignItems="center">
                {navItems.map((item) => (
                    <MenuItem key={item.url} label={t(item.labelKey)} url={item.url} icon={item.icon} onClick={onClose} />
                ))}
            </VStack>
        </VStack>
    );
};

const Layout = () => {
    const { t } = useTranslation("common");
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);

    return (
        <BrandingProvider>
        <AccountScoringProvider>
        <AgentTypesProvider>
        <RecruitmentDashboardDataProvider>
        <SalesDashboardDataProvider>
            <HStack w={'full'} minH={'100vh'} gap={0} alignItems={'flex-start'}>
                {/* Desktop sidebar */}
                <Box minW="80px" display={{ base: 'none', md: 'block' }}>
                    <Box pos={'fixed'} w="80px" h={'100vh'} zIndex={20}>
                        <SidebarNav t={t} />
                    </Box>
                </Box>

                {/* Mobile drawer */}
                <DrawerRoot
                    open={isSidebarOpen}
                    onOpenChange={(e) => setIsSidebarOpen(e.open)}
                    placement="start"
                >
                    <DrawerBackdrop />
                    <DrawerPositioner>
                        <DrawerContent p={0} maxW="80px" bg="#131019">
                            <SidebarNav t={t} onClose={() => setIsSidebarOpen(false)} />
                        </DrawerContent>
                    </DrawerPositioner>
                </DrawerRoot>

                <PageHeaderProvider>
                    <VStack alignItems={'flex-start'} w={'full'} minW={0} minH={'100vh'} gap={0} bg="#F6F6FB">
                        <TopBar onSidebarOpen={() => setIsSidebarOpen(true)} />
                        <VStack w={'100%'} minW={0} maxW={'full'} py={6} px={{ base: 4, md: 6 }} pt={{ base: 20, md: 2 }}>
                            <Outlet />
                        </VStack>
                    </VStack>
                </PageHeaderProvider>
            </HStack>
        </SalesDashboardDataProvider>
        </RecruitmentDashboardDataProvider>
        </AgentTypesProvider>
        </AccountScoringProvider>
        </BrandingProvider>
    );
};

export default Layout;
